import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import { Edit, Delete } from '@mui/icons-material';
import TableCell from '../../../components/TableCell';
import { formatDate } from '../../../resources/functions';
import axios from '../../../resources/axios';
import { selectAdmins } from '../../../redux/slices/adminsSlice';

interface UserSubscription {
  _id: string;
  user: {
    _id: string;
    phone: string;
    firstName?: string;
    lastName?: string;
  };
  subscriptionPlan: {
    _id: string;
    name: string;
    price: number;
  };
  city: {
    _id: string;
    cityName: string;
  };
  createdAt: string;
  expiresAt: string;
  status: string;
  expired: boolean;
  activateAdminLogin?: string;
  deactivateAdminLogin?: string;
  contractActions?: any;
  contractDurations?: any;
}

const UserSubscriptionsTab = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const adminInfo = useSelector(selectAdmins);
  const [userSubscriptions, setUserSubscriptions] = useState<UserSubscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(20);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' | 'warning' });
  const [deactivateDialog, setDeactivateDialog] = useState<{ open: boolean; subscription: UserSubscription | null }>({
    open: false,
    subscription: null
  });
  const [deactivating, setDeactivating] = useState(false);

  useEffect(() => {
    fetchUserSubscriptions();
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminInfo.currentCity]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const fetchUserSubscriptions = async () => {
    setLoading(true);
    try {
      const userInfo = JSON.parse(localStorage.getItem('STORE_USER_INFO') || '{}');
      const token = userInfo.token;

      console.log('Fetching user subscriptions (all cities)');

      const response = await axios.get('/subscription/admin/user_subscriptions', {
        headers: {
          'x-access-token': token
        },
        params: {
          limit: 1000,
          page: 1
        }
      });

      console.log('User subscriptions response:', response.data);

      if (response.data?.data?.subscriptions) {
        const allSubscriptions = response.data.data.subscriptions;
        console.log('All subscriptions:', allSubscriptions);
        
        const filteredByCity = adminInfo.currentCity 
          ? allSubscriptions.filter((sub: UserSubscription) => sub.city?._id === adminInfo.currentCity)
          : allSubscriptions;
        
        console.log('Filtered by city:', filteredByCity);
        setUserSubscriptions(filteredByCity);
      } else {
        console.log('No subscriptions found in response structure');
        console.log('Response data:', response.data);
      }
    } catch (error) {
      console.error('Error fetching user subscriptions:', error);
      setSnackbar({ open: true, message: t('subscriptions.errorLoading'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return { label: t('subscriptions.statusActive'), color: 'success' as const };
      case 'expired':
        return { label: t('subscriptions.statusExpired'), color: 'default' as const };
      case 'admin_canceled':
        return { label: t('subscriptions.statusCanceled'), color: 'error' as const };
      default:
        return { label: status, color: 'default' as const };
    }
  };

  const getUserDisplayName = (user: UserSubscription['user']) => {
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    return user.phone;
  };

  const handleDeactivate = async () => {
    if (!deactivateDialog.subscription || deactivating) return;

    setDeactivating(true);
    try {
      const userInfo = JSON.parse(localStorage.getItem('STORE_USER_INFO') || '{}');
      const token = userInfo.token;

      await axios.post(
        '/subscription/admin/deactivate',
        {
          userId: deactivateDialog.subscription.user._id,
          userSubscriptionId: deactivateDialog.subscription._id
        },
        {
          headers: {
            'x-access-token': token
          }
        }
      );

      setSnackbar({ open: true, message: t('subscriptions.deactivateSuccess'), severity: 'success' });
      setDeactivateDialog({ open: false, subscription: null });
      fetchUserSubscriptions();
    } catch (error) {
      console.error('Error deactivating subscription:', error);
      setSnackbar({ open: true, message: t('subscriptions.deactivateError'), severity: 'error' });
      setDeactivateDialog({ open: false, subscription: null });
    } finally {
      setDeactivating(false);
    }
  };

  const handleEdit = (subscription: UserSubscription) => {
    console.log('Edit subscription:', subscription);
    setSnackbar({ open: true, message: t('subscriptions.editInProgress'), severity: 'info' });
  };

  const filteredSubscriptions = userSubscriptions.filter(sub => {
    const query = searchQuery.toLowerCase();
    const userName = getUserDisplayName(sub.user).toLowerCase();
    const phone = sub.user.phone.toLowerCase();
    const planName = sub.subscriptionPlan.name.toLowerCase();
    
    return userName.includes(query) || phone.includes(query) || planName.includes(query);
  });

  const totalPages = Math.ceil(filteredSubscriptions.length / rowsPerPage);
  const paginatedSubscriptions = filteredSubscriptions.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder={t('subscriptions.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2C2C2C',
            borderRadius: 2,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                border: 'none',
              },
            },
          }}
        />
      </Box>

      {paginatedSubscriptions.length > 0 && (
        <>
          <TableContainer sx={{ mx: -2.5 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>{t('subscriptions.colUser')}</TableCell>
                  <TableCell>{t('subscriptions.colPlan')}</TableCell>
                  <TableCell>{t('subscriptions.colStartDate')}</TableCell>
                  <TableCell>{t('subscriptions.colEndDate')}</TableCell>
                  <TableCell>{t('subscriptions.colStatus')}</TableCell>
                  <TableCell>{t('subscriptions.colPrice')}</TableCell>
                  <TableCell align='right'>{t('subscriptions.colActions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedSubscriptions.map((subscription, index) => {
                  const statusConfig = getStatusConfig(subscription.status);
                  const globalIndex = (page - 1) * rowsPerPage + index + 1;
                  
                  return (
                    <TableRow hover key={subscription._id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: theme.palette.mode === 'light' ? '#E3F2FD' : 'rgba(33, 150, 243, 0.2)',
                              color: '#2196F3',
                              fontSize: 14,
                              fontWeight: 600,
                              borderRadius: '8px'
                            }}
                          >
                            #{globalIndex}
                          </Avatar>
                        </Box>
                      </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: theme.palette.mode === 'light' ? '#FFF3E0' : 'rgba(255, 152, 0, 0.2)',
                            color: '#FF9800',
                            borderRadius: '8px'
                          }}
                        >
                          <PersonIcon />
                        </Avatar>
                        <Box>
                          <Box sx={{ fontWeight: 500 }}>{getUserDisplayName(subscription.user)}</Box>
                          <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
                            {subscription.user.phone}
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{subscription.subscriptionPlan.name}</TableCell>
                    <TableCell>{formatDate(subscription.createdAt, { time: true })}</TableCell>
                    <TableCell>{formatDate(subscription.expiresAt, { time: true })}</TableCell>
                    <TableCell>
                      <Chip
                        label={statusConfig.label}
                        color={statusConfig.color}
                        size="small"
                        sx={{ borderRadius: 1 }}
                      />
                    </TableCell>
                    <TableCell>{subscription.subscriptionPlan.price} ₴</TableCell>
                    <TableCell align='right'>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title={t('subscriptions.edit')}>
                          <IconButton
                            size='medium'
                            onClick={() => handleEdit(subscription)}
                            sx={{
                              bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                              color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                              borderRadius: '8px',
                              '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
                            }}
                          >
                            <Edit fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('subscriptions.deactivate')}>
                          <IconButton
                            size='medium'
                            disabled={subscription.expired || subscription.status === 'admin_canceled'}
                            onClick={() => setDeactivateDialog({ open: true, subscription })}
                            sx={{
                              bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                              color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                              borderRadius: '8px',
                              '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
                            }}
                          >
                            <Delete fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.mode === 'light' ? '#424242' : '#4ECDC4',
                    color: theme.palette.mode === 'light' ? '#FFF' : '#000',
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'light' ? '#616161' : '#3BA99F',
                    }
                  }
                }
              }}
            />
          </Box>
        )}
      </>
      )}

      {filteredSubscriptions.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          {searchQuery ? t('subscriptions.nothingFound') : t('subscriptions.noUserSubscriptions')}
        </Box>
      )}

      <Dialog
        open={deactivateDialog.open}
        onClose={() => setDeactivateDialog({ open: false, subscription: null })}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 400
          }
        }}
      >
        <DialogTitle>{t('subscriptions.deactivateTitle')}</DialogTitle>
        <DialogContent>
          {t('subscriptions.deactivateConfirm', {
            name: deactivateDialog.subscription
              ? getUserDisplayName(deactivateDialog.subscription.user)
              : ''
          })}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeactivateDialog({ open: false, subscription: null })}
            disabled={deactivating}
            sx={{ borderRadius: 1 }}
          >
            {t('subscriptions.cancel')}
          </Button>
          <Button
            onClick={handleDeactivate}
            variant="contained"
            color="error"
            disabled={deactivating}
            sx={{ borderRadius: 1 }}
          >
            {deactivating ? t('subscriptions.deactivating') : t('subscriptions.deactivate')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 1 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserSubscriptionsTab;
